// src/pages/ReporteMorosidadConfigurable.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    collection,
    doc,
    onSnapshot,
    query,
    orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { esCasaCaseta } from '../utils/morosoUtils';
import ExcelJS from 'exceljs';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    Cell,
    PieChart,
    Pie,
    LabelList,
    AreaChart,
    Area,
} from 'recharts';
import {
    FaChevronLeft,
    FaChartBar,
    FaChartPie,
    FaFilePdf,
    FaFileExcel,
    FaSlidersH,
    FaCalendarAlt,
    FaCheckCircle,
    FaExclamationTriangle,
    FaHandshake,
    FaShieldAlt,
    FaHome,
    FaCoins,
    FaSearch,
    FaSyncAlt,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './ReporteMorosidadConfigurable.css';

// Plantillas predeterminadas de rangos
const RANGOS_PRESET_3 = [
    {
        id: 'r1',
        etiqueta: 'Casas en $0.00 Y CON SALDO A FAVOR',
        min: 0,
        max: 0,
        esCeroOSaldoFavor: true,
        sinLimite: false,
        color: '#059669', // Verde Zen
    },
    {
        id: 'r2',
        etiqueta: 'Casas con Mora de $0.01 a $2,550 Aceptable por ley',
        min: 0.01,
        max: 2550,
        esCeroOSaldoFavor: false,
        sinLimite: false,
        color: '#0284c7', // Azul Pizarra
    },
    {
        id: 'r3',
        etiqueta: 'Casas con Mora mayor a $2,551',
        min: 2550.01,
        max: 999999999,
        esCeroOSaldoFavor: false,
        sinLimite: true,
        color: '#dc2626', // Rojo Carmesí
    },
];

const RANGOS_PRESET_2 = [
    {
        id: 'r1',
        etiqueta: 'Al corriente / Saldo a favor ($0.00)',
        min: 0,
        max: 0,
        esCeroOSaldoFavor: true,
        sinLimite: false,
        color: '#059669',
    },
    {
        id: 'r2',
        etiqueta: 'Viviendas con adeudo pendiente (> $0.00)',
        min: 0.01,
        max: 999999999,
        esCeroOSaldoFavor: false,
        sinLimite: true,
        color: '#dc2626',
    },
];

const RANGOS_PRESET_1 = [
    {
        id: 'r1',
        etiqueta: 'Padrón general con morosidad activa',
        min: 0.01,
        max: 999999999,
        esCeroOSaldoFavor: false,
        sinLimite: true,
        color: '#dc2626',
    },
];

const RANGOS_PRESET_4 = [
    {
        id: 'r1',
        etiqueta: 'Casas en $0.00 Y CON SALDO A FAVOR',
        min: 0,
        max: 0,
        esCeroOSaldoFavor: true,
        sinLimite: false,
        color: '#059669',
    },
    {
        id: 'r2',
        etiqueta: 'Mora leve (1 a 3 meses / hasta $2,550)',
        min: 0.01,
        max: 2550,
        esCeroOSaldoFavor: false,
        sinLimite: false,
        color: '#0284c7',
    },
    {
        id: 'r3',
        etiqueta: 'Mora moderada (4 a 6 meses / $2,551 a $5,100)',
        min: 2550.01,
        max: 5100,
        esCeroOSaldoFavor: false,
        sinLimite: false,
        color: '#d97706',
    },
    {
        id: 'r4',
        etiqueta: 'Mora crítica / Jurídica (Mayor a $5,100)',
        min: 5100.01,
        max: 999999999,
        esCeroOSaldoFavor: false,
        sinLimite: true,
        color: '#dc2626',
    },
];

const formatCurrency = (val) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(num);
};

const formatFechaCST = (date = new Date()) => {
    return new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(date);
};

const ReporteMorosidadConfigurable = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fraccionamientoId } = useAuth();

    // Estados de datos
    const [loading, setLoading] = useState(true);
    const [fraccData, setFraccData] = useState(null);
    const [casas, setCasas] = useState([]);
    const [ingresos, setIngresos] = useState([]);

    // Estados de configuración interactiva
    const [numRangos, setNumRangos] = useState(3);
    const [rangos, setRangos] = useState(RANGOS_PRESET_3);
    const [tipoGrafica, setTipoGrafica] = useState('barras'); // 'barras' | 'pastel'
    const [tipoRecuperacion, setTipoRecuperacion] = useState('mes'); // 'mes' | 'anio'
    const [incluirRecuperacionEnPDF, setIncluirRecuperacionEnPDF] = useState(true);
    const [filtroTabla, setFiltroTabla] = useState('');
    const [alcanceDescarga, setAlcanceDescarga] = useState('morosos'); // 'morosos' | 'todos' | 'rango'
    const [rangoSeleccionadoFiltro, setRangoSeleccionadoFiltro] = useState('todos');

    // Cargar datos de Fraccionamiento
    useEffect(() => {
        if (!fraccionamientoId) return;
        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccRef, (snapshot) => {
            if (snapshot.exists()) {
                setFraccData(snapshot.data());
            }
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Cargar Casas en tiempo real
    useEffect(() => {
        if (!fraccionamientoId) return;
        setLoading(true);
        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const qCasas = query(casasRef, orderBy('numero'));
        const unsubscribe = onSnapshot(qCasas, (snapshot) => {
            const list = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((c) => !esCasaCaseta(c)); // Filtrar caseta
            setCasas(list);
            setLoading(false);
        }, (err) => {
            console.error('Error cargando casas:', err);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Cargar Ingresos históricos para Recuperación de Cartera
    useEffect(() => {
        if (!fraccionamientoId) return;
        const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
        const unsubscribe = onSnapshot(ingresosRef, (snapshot) => {
            const list = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((i) => !i.deleted && Number(i.montoNeto ?? i.monto ?? 0) > 0);
            setIngresos(list);
        }, (err) => {
            console.warn('Error leyendo ingresos para recuperación:', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Cambiar cantidad de rangos
    const handleCambiarNumRangos = (num) => {
        setNumRangos(num);
        if (num === 1) setRangos(JSON.parse(JSON.stringify(RANGOS_PRESET_1)));
        else if (num === 2) setRangos(JSON.parse(JSON.stringify(RANGOS_PRESET_2)));
        else if (num === 3) setRangos(JSON.parse(JSON.stringify(RANGOS_PRESET_3)));
        else if (num === 4) setRangos(JSON.parse(JSON.stringify(RANGOS_PRESET_4)));
    };

    // Actualizar un rango específico
    const handleActualizarRango = (index, campo, valor) => {
        setRangos((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [campo]: valor };
            return next;
        });
    };

    // Restablecer rangos predeterminados
    const handleRestablecerRangos = () => {
        handleCambiarNumRangos(3);
        Swal.fire({
            title: 'Rangos Restablecidos',
            text: 'Se han cargado los rangos de la plantilla estándar ($0.00, $0.01 - $2,550 y > $2,550).',
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
        });
    };

    // Logo del fraccionamiento
    const logoFraccionamiento = useMemo(() => {
        return fraccData?.configuracionRecibos?.logoUrl || fraccData?.logoUrl || null;
    }, [fraccData]);

    const nombreFraccionamiento = fraccData?.nombre || 'Fraccionamiento Residencial';
    const cuotaMensual = Number(
        fraccData?.configuracionFinanciera?.cuotaMensual ||
        fraccData?.configuracionFinanciera?.montoMensual ||
        850
    );

    // Clasificación de Casas por Rangos
    const analisisRangos = useMemo(() => {
        const totalCasas = casas.length;
        if (totalCasas === 0) return { rangosCalculados: [], totalAdeudoGlobal: 0, casasClasificadas: [] };

        let totalAdeudoGlobal = 0;
        const mapaCasasPorRango = {};
        rangos.forEach((r) => {
            mapaCasasPorRango[r.id] = {
                rango: r,
                casas: [],
                montoTotal: 0,
            };
        });

        // Clasificar cada casa
        const casasProcesadas = casas.map((casa) => {
            const adeudo = Number(casa.adeudo || 0);
            const saldoFavor = Number(casa.saldoFavor || 0);
            const adeudoCapital = Number(casa.adeudoCapital || (adeudo > 0 ? adeudo : 0));
            const adeudoIntereses = Number(casa.adeudoIntereses || 0);
            const cuotasExtras = Number(casa.cuotasExtras || 0);

            if (adeudo > 0) {
                totalAdeudoGlobal += adeudo;
            }

            // Identificar en qué rango cae
            let rangoAsignado = null;
            for (let i = 0; i < rangos.length; i++) {
                const r = rangos[i];
                if (r.esCeroOSaldoFavor) {
                    if (adeudo <= 0 || saldoFavor > 0) {
                        rangoAsignado = r;
                        break;
                    }
                } else if (r.sinLimite) {
                    if (adeudo >= Number(r.min)) {
                        rangoAsignado = r;
                        break;
                    }
                } else {
                    if (adeudo >= Number(r.min) && adeudo <= Number(r.max)) {
                        rangoAsignado = r;
                        break;
                    }
                }
            }

            // Fallback si no entró en ninguno
            if (!rangoAsignado) {
                rangoAsignado = rangos[rangos.length - 1];
            }

            const tieneConvenio = Boolean(
                casa.tieneConvenio ||
                casa.acuerdoPago ||
                casa.convenioActivo ||
                casa.convenio ||
                (casa.notasConvenio && String(casa.notasConvenio).trim().length > 0)
            );

            // Estimar meses de adeudo
            const mesesAdeudoEstimados = adeudoCapital > 0 && cuotaMensual > 0
                ? Math.max(1, Math.round(adeudoCapital / cuotaMensual))
                : (adeudo > 0 ? 1 : 0);

            const esAbonoParcial = adeudoCapital > 0 && cuotaMensual > 0 && (adeudoCapital % cuotaMensual !== 0);

            const infoCasa = {
                ...casa,
                numero: casa.numero || casa.numeroCasa || casa.id,
                propietario: casa.propietario || casa.nombre_residente || casa.nombre || 'Residente',
                adeudo,
                adeudoCapital,
                adeudoIntereses,
                cuotasExtras,
                saldoFavor,
                tieneConvenio,
                mesesAdeudoEstimados,
                esAbonoParcial,
                rangoId: rangoAsignado.id,
                rangoEtiqueta: rangoAsignado.etiqueta,
                rangoColor: rangoAsignado.color,
            };

            if (mapaCasasPorRango[rangoAsignado.id]) {
                mapaCasasPorRango[rangoAsignado.id].casas.push(infoCasa);
                mapaCasasPorRango[rangoAsignado.id].montoTotal += adeudo;
            }

            return infoCasa;
        });

        const rangosCalculados = rangos.map((r) => {
            const entry = mapaCasasPorRango[r.id] || { casas: [], montoTotal: 0 };
            const count = entry.casas.length;
            const pctCasas = totalCasas > 0 ? (count / totalCasas) * 100 : 0;
            const pctMonto = totalAdeudoGlobal > 0 ? (entry.montoTotal / totalAdeudoGlobal) * 100 : 0;
            return {
                ...r,
                conteo: count,
                porcentajeCasas: pctCasas,
                montoTotal: entry.montoTotal,
                porcentajeMonto: pctMonto,
                casas: entry.casas,
            };
        });

        return {
            rangosCalculados,
            totalAdeudoGlobal,
            casasClasificadas: casasProcesadas,
        };
    }, [casas, rangos, cuotaMensual]);

    // Resumen Ejecutivo de Métricas Globales
    const metricasGlobales = useMemo(() => {
        const total = casas.length;
        const alCorriente = casas.filter((c) => Number(c.adeudo || 0) <= 0 || Number(c.saldoFavor || 0) > 0).length;
        const morosos = total - alCorriente;
        const totalAdeudo = analisisRangos.totalAdeudoGlobal;
        const tasaMorosidad = total > 0 ? (morosos / total) * 100 : 0;

        // Casas morosas con convenio vs sin convenio
        const casasMorosas = analisisRangos.casasClasificadas.filter((c) => c.adeudo > 0);
        const conAcuerdo = casasMorosas.filter((c) => c.tieneConvenio).length;
        const sinAcuerdo = casasMorosas.length - conAcuerdo;

        return {
            total,
            alCorriente,
            morosos,
            totalAdeudo,
            tasaMorosidad,
            conAcuerdo,
            sinAcuerdo,
        };
    }, [casas, analisisRangos]);

    // Aging de Cartera (Antigüedad de adeudos)
    const agingData = useMemo(() => {
        const casasMorosas = analisisRangos.casasClasificadas.filter((c) => c.adeudo > 0);
        let c1_3 = 0, m1_3 = 0;
        let c4_6 = 0, m4_6 = 0;
        let c7_12 = 0, m7_12 = 0;
        let cMas12 = 0, mMas12 = 0;

        casasMorosas.forEach((c) => {
            const meses = c.mesesAdeudoEstimados;
            const monto = c.adeudo;
            if (meses <= 3) {
                c1_3++;
                m1_3 += monto;
            } else if (meses <= 6) {
                c4_6++;
                m4_6 += monto;
            } else if (meses <= 12) {
                c7_12++;
                m7_12 += monto;
            } else {
                cMas12++;
                mMas12 += monto;
            }
        });

        return [
            { label: '1 a 3 meses (Mora Reciente)', count: c1_3, monto: m1_3, color: '#0284c7' },
            { label: '4 a 6 meses (Mora Moderada)', count: c4_6, monto: m4_6, color: '#d97706' },
            { label: '7 a 12 meses (Mora Alta)', count: c7_12, monto: m7_12, color: '#ea580c' },
            { label: '> 12 meses (Mora Crítica / Legal)', count: cMas12, monto: mMas12, color: '#dc2626' },
        ];
    }, [analisisRangos]);

    // Análisis Pareto / Top 5 Deudores
    const topDeudores = useMemo(() => {
        const casasMorosas = [...analisisRangos.casasClasificadas.filter((c) => c.adeudo > 0)];
        casasMorosas.sort((a, b) => b.adeudo - a.adeudo);
        const top5 = casasMorosas.slice(0, 5);
        const sumaTop5 = top5.reduce((acc, c) => acc + c.adeudo, 0);
        const pctTop5 = metricasGlobales.totalAdeudo > 0 ? (sumaTop5 / metricasGlobales.totalAdeudo) * 100 : 0;
        return { list: top5, sumaTop5, pctTop5 };
    }, [analisisRangos, metricasGlobales]);

    // Datos para la Gráfica de Barras de Rangos (Coincidiendo con imagen 1 y 2 del usuario)
    const chartRangosBarData = useMemo(() => {
        const data = analisisRangos.rangosCalculados.map((r) => ({
            name: r.etiqueta,
            Casas: r.conteo,
            Monto: r.montoTotal,
            porcentaje: r.porcentajeCasas.toFixed(1) + '%',
            color: r.color,
        }));

        // Añadir barra de Total como en la imagen del usuario
        data.push({
            name: 'Total',
            Casas: metricasGlobales.total,
            Monto: metricasGlobales.totalAdeudo,
            porcentaje: '100%',
            color: '#d97706',
        });

        return data;
    }, [analisisRangos, metricasGlobales]);

    // Datos para la Gráfica de Pastel / Dona
    const chartRangosPieData = useMemo(() => {
        return analisisRangos.rangosCalculados.map((r) => ({
            name: r.etiqueta,
            value: r.conteo,
            monto: r.montoTotal,
            color: r.color,
        }));
    }, [analisisRangos]);

    // Serie de Recuperación de Cartera (Histórico por mes o por año)
    const recuperacionCarteraData = useMemo(() => {
        const agrupado = {};

        ingresos.forEach((ing) => {
            const fecha = ing.fecha?.toDate ? ing.fecha.toDate() : new Date(ing.fecha || ing.createdAt);
            if (isNaN(fecha.getTime())) return;

            let key;
            if (tipoRecuperacion === 'mes') {
                const mesStr = fecha.toLocaleString('es-MX', { month: 'short' });
                key = `${mesStr} ${fecha.getFullYear()}`;
            } else {
                key = String(fecha.getFullYear());
            }

            const monto = Number(ing.montoNeto ?? ing.monto ?? 0);
            agrupado[key] = (agrupado[key] || 0) + monto;
        });

        // Ordenar cronológicamente o devolver como lista
        const keys = Object.keys(agrupado);
        if (keys.length === 0) {
            // Datos ilustrativos si es un fraccionamiento recién registrado sin movimientos
            return [
                { periodo: 'Ago 2026', recuperado: 12500 },
                { periodo: 'Sep 2026', recuperado: 18400 },
                { periodo: 'Oct 2026', recuperado: 21250 },
            ];
        }

        return keys.map((k) => ({
            periodo: k,
            recuperado: agrupado[k],
        }));
    }, [ingresos, tipoRecuperacion]);

    // Casas filtradas para la tabla
    const casasFiltradasTabla = useMemo(() => {
        let list = analisisRangos.casasClasificadas;

        if (rangoSeleccionadoFiltro !== 'todos') {
            list = list.filter((c) => c.rangoId === rangoSeleccionadoFiltro);
        }

        if (filtroTabla.trim()) {
            const term = filtroTabla.trim().toLowerCase();
            list = list.filter((c) =>
                String(c.numero).toLowerCase().includes(term) ||
                String(c.propietario).toLowerCase().includes(term)
            );
        }

        return list;
    }, [analisisRangos, rangoSeleccionadoFiltro, filtroTabla]);

    // Generador y Descargador de Reporte PDF Ejecutivo
    const handleGenerarPDF = useCallback(() => {
        try {
            const printWin = window.open('', '_blank');
            if (!printWin) {
                Swal.fire('Atención', 'Permite las ventanas emergentes en tu navegador para ver y descargar el PDF.', 'warning');
                return;
            }

            const fechaEmision = formatFechaCST();
            const casasParaReporte = alcanceDescarga === 'todos'
                ? analisisRangos.casasClasificadas
                : analisisRangos.casasClasificadas.filter((c) => c.adeudo > 0);

            // Generar tabla de rangos en HTML
            const rangosHtmlRows = analisisRangos.rangosCalculados.map((r) => `
                <tr>
                    <td style="padding: 6px 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${r.color}; margin-right: 6px;"></span>
                        ${r.etiqueta}
                    </td>
                    <td style="padding: 6px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: 700; color: #0f172a;">
                        ${r.conteo}
                    </td>
                    <td style="padding: 6px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: 600; color: #059669;">
                        ${r.porcentajeCasas.toFixed(1)}%
                    </td>
                    <td style="padding: 6px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: 700; color: #1e293b;">
                        ${formatCurrency(r.montoTotal)}
                    </td>
                    <td style="padding: 6px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #64748b;">
                        ${r.porcentajeMonto.toFixed(1)}%
                    </td>
                </tr>
            `).join('');

            // Generar barras visuales en HTML/SVG para impresión
            const maxConteo = Math.max(...analisisRangos.rangosCalculados.map((r) => r.conteo), metricasGlobales.total, 1);
            const barsSvgItems = chartRangosBarData.map((d, idx) => {
                const barHeight = Math.max(10, Math.round((d.Casas / maxConteo) * 110));
                const y = 130 - barHeight;
                const x = 30 + idx * 110;
                return `
                    <g>
                        <text x="${x + 35}" y="${y - 6}" font-size="11" font-weight="bold" fill="#0f172a" text-anchor="middle">${d.Casas}</text>
                        <rect x="${x}" y="${y}" width="70" height="${barHeight}" rx="5" fill="${d.color}" />
                        <text x="${x + 35}" y="148" font-size="9" fill="#475569" text-anchor="middle" font-weight="600">${d.name.length > 18 ? d.name.substring(0, 16) + '...' : d.name}</text>
                    </g>
                `;
            }).join('');

            // Gráfica de recuperación en SVG si está seleccionada
            let recuperacionSvgBlock = '';
            if (incluirRecuperacionEnPDF && recuperacionCarteraData.length > 0) {
                const maxRecup = Math.max(...recuperacionCarteraData.map((d) => d.recuperado), 1);
                const recupBars = recuperacionCarteraData.slice(-6).map((d, idx) => {
                    const h = Math.max(8, Math.round((d.recuperado / maxRecup) * 90));
                    const y = 110 - h;
                    const x = 30 + idx * 80;
                    return `
                        <g>
                            <text x="${x + 25}" y="${y - 4}" font-size="9" font-weight="bold" fill="#059669" text-anchor="middle">$${Math.round(d.recuperado / 1000)}k</text>
                            <rect x="${x}" y="${y}" width="50" height="${h}" rx="4" fill="#10b981" />
                            <text x="${x + 25}" y="125" font-size="8.5" fill="#64748b" text-anchor="middle">${d.periodo}</text>
                        </g>
                    `;
                }).join('');

                recuperacionSvgBlock = `
                    <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 18px; margin-top: 14px; page-break-inside: avoid;">
                        <h4 style="margin: 0 0 10px; font-size: 12px; color: #0f172a; text-transform: uppercase; letter-spacing: 0.05em;">
                            Recuperación Histórica de Cartera (${tipoRecuperacion === 'mes' ? 'Mensual' : 'Anual'})
                        </h4>
                        <svg width="520" height="135" style="display: block; margin: 0 auto; overflow: visible;">
                            <line x1="15" y1="110" x2="505" y2="110" stroke="#cbd5e1" stroke-width="1.5" />
                            ${recupBars}
                        </svg>
                    </div>
                `;
            }

            // Generar filas detalladas de casas
            const casasHtmlRows = casasParaReporte.map((c, idx) => `
                <tr style="background: ${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #1e293b;">
                        ${c.numero}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; color: #334155;">
                        ${c.propietario}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; color: #475569;">
                        ${c.mesesAdeudoEstimados} mes(es)
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: center;">
                        <span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; background: ${c.esAbonoParcial ? '#dcfce7' : '#f1f5f9'}; color: ${c.esAbonoParcial ? '#166534' : '#475569'};">
                            ${c.esAbonoParcial ? 'ABONO PARCIAL' : 'PENDIENTE TOTAL'}
                        </span>
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #334155;">
                        ${formatCurrency(c.adeudoCapital)}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #dc2626;">
                        ${formatCurrency(c.adeudoIntereses)}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #d97706;">
                        ${formatCurrency(c.cuotasExtras)}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: 800; color: #0f172a;">
                        ${formatCurrency(c.adeudo)}
                    </td>
                    <td style="padding: 7px 10px; border-bottom: 1px solid #e2e8f0; text-align: center;">
                        <span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; background: ${c.tieneConvenio ? '#e0e7ff' : '#fee2e2'}; color: ${c.tieneConvenio ? '#3730a3' : '#991b1b'};">
                            ${c.tieneConvenio ? 'CON ACUERDO' : 'SIN ACUERDO'}
                        </span>
                    </td>
                </tr>
            `).join('');

            const logoFraccTag = logoFraccionamiento
                ? `<img src="${logoFraccionamiento}" alt="Logo Fraccionamiento" style="max-height: 58px; max-width: 140px; object-fit: contain; margin-right: 18px;" />`
                : '';

            const htmlContent = `
                <!doctype html>
                <html lang="es">
                <head>
                    <meta charset="utf-8" />
                    <title>Reporte Ejecutivo de Morosidad - ${nombreFraccionamiento}</title>
                    <style>
                        @page {
                            size: letter portrait;
                            margin: 12mm 14mm 14mm 14mm;
                        }
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                            color: #1e293b;
                            background: #ffffff;
                            margin: 0;
                            padding: 0;
                            font-size: 11px;
                            line-height: 1.4;
                        }
                        .header-table {
                            width: 100%;
                            border-bottom: 2px solid #1e3a8a;
                            padding-bottom: 12px;
                            margin-bottom: 16px;
                        }
                        .kpi-row {
                            display: flex;
                            gap: 10px;
                            margin-bottom: 16px;
                        }
                        .kpi-box {
                            flex: 1;
                            background: #f8fafc;
                            border: 1px solid #e2e8f0;
                            border-radius: 8px;
                            padding: 8px 12px;
                            text-align: center;
                        }
                        .kpi-title {
                            font-size: 9px;
                            font-weight: 700;
                            color: #64748b;
                            text-transform: uppercase;
                            letter-spacing: 0.05em;
                        }
                        .kpi-val {
                            font-size: 16px;
                            font-weight: 800;
                            color: #0f172a;
                            margin-top: 2px;
                        }
                        table.data-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 10px;
                            font-size: 10px;
                        }
                        table.data-table th {
                            background: #f1f5f9;
                            padding: 8px 10px;
                            font-size: 9px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.05em;
                            color: #334155;
                            border-bottom: 2px solid #cbd5e1;
                        }
                        .page-break {
                            page-break-before: always;
                        }
                        .footer-brand {
                            margin-top: 28px;
                            padding-top: 12px;
                            border-top: 1px solid #e2e8f0;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            font-size: 9px;
                            color: #64748b;
                        }
                        .signatures-block {
                            display: flex;
                            justify-content: space-around;
                            margin-top: 36px;
                            page-break-inside: avoid;
                        }
                        .sig-line {
                            width: 200px;
                            text-align: center;
                            border-top: 1px solid #94a3b8;
                            padding-top: 6px;
                            font-size: 10px;
                            color: #334155;
                        }
                    </style>
                </head>
                <body>
                    <!-- ENCABEZADO FORMAL INSTITUCIONAL -->
                    <table class="header-table">
                        <tr>
                            <td style="vertical-align: middle; width: 140px;">
                                ${logoFraccTag}
                            </td>
                            <td style="vertical-align: middle;">
                                <h1 style="margin: 0; font-size: 18px; color: #0f172a; text-transform: uppercase; letter-spacing: -0.01em;">
                                    ${nombreFraccionamiento}
                                </h1>
                                <h2 style="margin: 3px 0 0; font-size: 13px; font-weight: 600; color: #1e3a8a;">
                                    REPORTE EJECUTIVO DE MOROSIDAD Y CARTERA VENCIDA
                                </h2>
                                <p style="margin: 2px 0 0; font-size: 10px; color: #64748b;">
                                    Emisión: <strong>${fechaEmision}</strong> (Tiempo del Centro de México)
                                </p>
                            </td>
                        </tr>
                    </table>

                    <!-- TARJETAS DE KPIS EJECUTIVOS -->
                    <div class="kpi-row">
                        <div class="kpi-box">
                            <div class="kpi-title">Total Viviendas</div>
                            <div class="kpi-val">${metricasGlobales.total}</div>
                        </div>
                        <div class="kpi-box">
                            <div class="kpi-title">Al Corriente</div>
                            <div class="kpi-val" style="color: #059669;">${metricasGlobales.alCorriente}</div>
                        </div>
                        <div class="kpi-box">
                            <div class="kpi-title">Viviendas en Mora</div>
                            <div class="kpi-val" style="color: #dc2626;">${metricasGlobales.morosos}</div>
                        </div>
                        <div class="kpi-box">
                            <div class="kpi-title">Tasa de Morosidad</div>
                            <div class="kpi-val" style="color: #ea580c;">${metricasGlobales.tasaMorosidad.toFixed(1)}%</div>
                        </div>
                        <div class="kpi-box">
                            <div class="kpi-title">Cartera Total</div>
                            <div class="kpi-val" style="color: #0f172a;">${formatCurrency(metricasGlobales.totalAdeudo)}</div>
                        </div>
                    </div>

                    <!-- RESUMEN DE RANGOS CONFIGURADOS -->
                    <h3 style="margin: 14px 0 6px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #0f172a;">
                        1. Segmentación Configurada de Cartera y Rangos de Morosidad
                    </h3>
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Rango / Segmento</th>
                                <th style="text-align: center;">Viviendas</th>
                                <th style="text-align: center;">% Viviendas</th>
                                <th style="text-align: right;">Cartera Acumulada</th>
                                <th style="text-align: right;">% Cartera</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rangosHtmlRows}
                        </tbody>
                    </table>

                    <!-- GRÁFICA VECTORIAL DE RANGOS -->
                    <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 18px; margin-top: 14px; page-break-inside: avoid;">
                        <h4 style="margin: 0 0 10px; font-size: 12px; color: #0f172a; text-transform: uppercase; letter-spacing: 0.05em;">
                            Distribución de Viviendas por Nivel de Morosidad
                        </h4>
                        <svg width="520" height="155" style="display: block; margin: 0 auto; overflow: visible;">
                            <line x1="20" y1="130" x2="500" y2="130" stroke="#cbd5e1" stroke-width="1.5" />
                            ${barsSvgItems}
                        </svg>
                    </div>

                    ${recuperacionSvgBlock}

                    <!-- RESUMEN DE ACUERDOS Y CONVENIOS (COLUMN T & U) -->
                    <div style="display: flex; gap: 12px; margin-top: 14px; page-break-inside: avoid;">
                        <div style="flex: 1; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 10px; padding: 10px 14px;">
                            <strong style="color: #065f46; font-size: 11px;">CONVENIOS ACTIVOS / ACUERDOS FORMALES:</strong>
                            <div style="font-size: 16px; font-weight: 800; color: #047857; margin-top: 2px;">
                                ${metricasGlobales.conAcuerdo} Viviendas
                            </div>
                            <small style="color: #047857;">Compromiso firmado de regularización en parcialidades</small>
                        </div>
                        <div style="flex: 1; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; padding: 10px 14px;">
                            <strong style="color: #991b1b; font-size: 11px;">SIN ACUERDO / GESTIÓN PENDIENTE:</strong>
                            <div style="font-size: 16px; font-weight: 800; color: #b91c1c; margin-top: 2px;">
                                ${metricasGlobales.sinAcuerdo} Viviendas
                            </div>
                            <small style="color: #b91c1c;">Requiere seguimiento o notificación administrativa</small>
                        </div>
                    </div>

                    <!-- SALTO DE PÁGINA PARA LA TABLA DETALLADA -->
                    <div class="page-break"></div>

                    <!-- TABLA DETALLADA DE RESIDENTES MOROSOS -->
                    <h3 style="margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #0f172a;">
                        2. Padrón Detallado de Viviendas y Desglose Contable
                    </h3>
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Casa</th>
                                <th>Residente</th>
                                <th style="text-align: center;">Meses</th>
                                <th style="text-align: center;">Condición</th>
                                <th style="text-align: right;">Capital</th>
                                <th style="text-align: right;">Intereses</th>
                                <th style="text-align: right;">Cuotas Ext.</th>
                                <th style="text-align: right;">Adeudo Total</th>
                                <th style="text-align: center;">Convenio</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${casasHtmlRows}
                        </tbody>
                        <tfoot>
                            <tr style="background: #f1f5f9; font-weight: 800;">
                                <td colspan="4" style="padding: 8px 10px; text-align: right; border-top: 2px solid #cbd5e1;">TOTAL CONSOLIDADO:</td>
                                <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #cbd5e1;">${formatCurrency(casasParaReporte.reduce((a, c) => a + c.adeudoCapital, 0))}</td>
                                <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #cbd5e1; color: #dc2626;">${formatCurrency(casasParaReporte.reduce((a, c) => a + c.adeudoIntereses, 0))}</td>
                                <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #cbd5e1; color: #d97706;">${formatCurrency(casasParaReporte.reduce((a, c) => a + c.cuotasExtras, 0))}</td>
                                <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #cbd5e1; font-size: 11px;">${formatCurrency(casasParaReporte.reduce((a, c) => a + c.adeudo, 0))}</td>
                                <td style="border-top: 2px solid #cbd5e1;"></td>
                            </tr>
                        </tfoot>
                    </table>

                    <!-- BLOQUE DE FIRMAS FORMALES -->
                    <div class="signatures-block">
                        <div class="sig-line">
                            <strong>Mesa Directiva / Tesorería</strong><br/>
                            ${nombreFraccionamiento}
                        </div>
                        <div class="sig-line">
                            <strong>Administración del Condominio</strong><br/>
                            Gestión y Auditoría Contable
                        </div>
                    </div>

                    <!-- PIE DE PÁGINA CON MARCA DOMUS HABITA -->
                    <div class="footer-brand">
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <img src="/Domus Icono1.png" onerror="this.src='/logo.png'" alt="Domus Habita" style="height: 22px; width: auto;" />
                            <strong>Domus Habita</strong> &mdash; Plataforma Integral de Gestión y Administración Condominal
                        </div>
                        <div>
                            Documento Auditado y Oficial &bull; Reporte Ejecutivo
                        </div>
                    </div>
                </body>
                </html>
            `;

            printWin.document.write(htmlContent);
            printWin.document.close();
            printWin.focus();
            setTimeout(() => {
                printWin.print();
            }, 600);
        } catch (error) {
            console.error('Error al generar PDF:', error);
            Swal.fire('Error', 'No se pudo generar la vista de descarga del reporte.', 'error');
        }
    }, [
        nombreFraccionamiento,
        logoFraccionamiento,
        metricasGlobales,
        analisisRangos,
        chartRangosBarData,
        incluirRecuperacionEnPDF,
        recuperacionCarteraData,
        tipoRecuperacion,
        alcanceDescarga,
    ]);

    // Generador y Descargador de Libro Excel (.xlsx) con ExcelJS
    const handleGenerarExcel = useCallback(async () => {
        try {
            const wb = new ExcelJS.Workbook();
            wb.creator = 'Domus Habita';
            wb.created = new Date();

            const currencyFmt = '$#,##0.00;[Red]$#,##0.00';
            const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
            const headerFont = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
            const borderThin = {
                top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            };

            // HOJA 1: RESUMEN Y RANGOS
            const ws1 = wb.addWorksheet('Resumen Ejecutivo');
            ws1.columns = [
                { width: 35 },
                { width: 16 },
                { width: 16 },
                { width: 22 },
                { width: 16 },
            ];

            ws1.mergeCells('A1:E1');
            ws1.getCell('A1').value = `${nombreFraccionamiento.toUpperCase()} - REPORTE EJECUTIVO DE MOROSIDAD`;
            ws1.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };

            ws1.mergeCells('A2:E2');
            ws1.getCell('A2').value = `Fecha de corte: ${formatFechaCST()} | Plataforma Domus Habita`;
            ws1.getCell('A2').font = { italic: true, size: 9, color: { argb: 'FF64748B' } };

            // KPIs
            ws1.addRow([]);
            ws1.addRow(['MÉTRICA GLOBAL', 'VALOR', '', '', '']);
            ws1.getRow(4).font = headerFont;
            ws1.getRow(4).fill = headerFill;

            ws1.addRow(['Total de Viviendas en Padrón', metricasGlobales.total]);
            ws1.addRow(['Viviendas al Corriente', metricasGlobales.alCorriente]);
            ws1.addRow(['Viviendas con Morosidad Activa', metricasGlobales.morosos]);
            ws1.addRow(['Tasa de Morosidad Global (%)', `${metricasGlobales.tasaMorosidad.toFixed(1)}%`]);
            ws1.addRow(['Cartera Vencida Total ($)', metricasGlobales.totalAdeudo]);
            ws1.getCell('B9').numFmt = currencyFmt;

            ws1.addRow([]);
            ws1.addRow(['SEGMENTACIÓN POR RANGOS DE MOROSIDAD', 'CASAS', '% CASAS', 'MONTO TOTAL', '% MONTO']);
            const rHeaderIdx = ws1.rowCount;
            ws1.getRow(rHeaderIdx).font = headerFont;
            ws1.getRow(rHeaderIdx).fill = headerFill;

            analisisRangos.rangosCalculados.forEach((r) => {
                const row = ws1.addRow([
                    r.etiqueta,
                    r.conteo,
                    `${r.porcentajeCasas.toFixed(1)}%`,
                    r.montoTotal,
                    `${r.porcentajeMonto.toFixed(1)}%`,
                ]);
                row.getCell(4).numFmt = currencyFmt;
            });

            // HOJA 2: DETALLE DE VIVIENDAS MOROSAS
            const ws2 = wb.addWorksheet('Detalle Viviendas');
            ws2.columns = [
                { header: 'Casa', key: 'numero', width: 12 },
                { header: 'Residente / Propietario', key: 'propietario', width: 32 },
                { header: 'Meses Adeudo', key: 'meses', width: 15 },
                { header: 'Condición Pago', key: 'condicion', width: 18 },
                { header: 'Adeudo Capital', key: 'capital', width: 18 },
                { header: 'Intereses', key: 'intereses', width: 16 },
                { header: 'Cuotas Extras', key: 'extras', width: 16 },
                { header: 'Adeudo Total', key: 'adeudo', width: 18 },
                { header: 'Estatus Acuerdo', key: 'acuerdo', width: 16 },
                { header: 'Rango Asignado', key: 'rango', width: 30 },
            ];

            ws2.getRow(1).font = headerFont;
            ws2.getRow(1).fill = headerFill;

            const casasExport = alcanceDescarga === 'todos'
                ? analisisRangos.casasClasificadas
                : analisisRangos.casasClasificadas.filter((c) => c.adeudo > 0);

            casasExport.forEach((c) => {
                const r = ws2.addRow({
                    numero: c.numero,
                    propietario: c.propietario,
                    meses: c.mesesAdeudoEstimados,
                    condicion: c.esAbonoParcial ? 'ABONO PARCIAL' : 'PENDIENTE TOTAL',
                    capital: c.adeudoCapital,
                    intereses: c.adeudoIntereses,
                    extras: c.cuotasExtras,
                    adeudo: c.adeudo,
                    acuerdo: c.tieneConvenio ? 'CON ACUERDO' : 'SIN ACUERDO',
                    rango: c.rangoEtiqueta,
                });
                r.getCell(5).numFmt = currencyFmt;
                r.getCell(6).numFmt = currencyFmt;
                r.getCell(7).numFmt = currencyFmt;
                r.getCell(8).numFmt = currencyFmt;
                r.eachCell((cell) => {
                    cell.border = borderThin;
                });
            });

            const buf = await wb.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Reporte_Morosidad_${nombreFraccionamiento.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);

            Swal.fire({
                title: 'Excel Generado',
                text: 'El archivo Excel se ha descargado correctamente con las fórmulas y hojas estructuradas.',
                icon: 'success',
                timer: 2500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error('Error al generar Excel:', error);
            Swal.fire('Error', 'No se pudo generar el archivo Excel.', 'error');
        }
    }, [nombreFraccionamiento, metricasGlobales, analisisRangos, alcanceDescarga]);

    return (
        <div className="reporte-morosidad-page">
            <div className="reporte-morosidad-container">
                {/* HEADER ZEN */}
                <header className="rm-header-card">
                    <div className="rm-header-left">
                        {logoFraccionamiento ? (
                            <img src={logoFraccionamiento} alt={nombreFraccionamiento} className="rm-fracc-logo" />
                        ) : (
                            <div className="rm-fracc-logo-fallback">
                                <FaHome />
                            </div>
                        )}
                        <div className="rm-header-titles">
                            <h1>Reporte Ejecutivo de Morosidad</h1>
                            <p>Análisis configurable de cartera vencida y recuperación</p>
                            <div className="rm-header-meta">
                                <strong>{nombreFraccionamiento}</strong> &bull; Cuota base: {formatCurrency(cuotaMensual)}
                            </div>
                        </div>
                    </div>

                    <div className="rm-header-actions">
                        <button
                            type="button"
                            className="rm-btn-back"
                            onClick={() => {
                                if (location.state?.originalSearch !== undefined) {
                                    navigate('/administracion/departamentos/residentes/morosos', {
                                        state: { search: location.state.originalSearch }
                                    });
                                } else {
                                    navigate(-1);
                                }
                            }}
                        >
                            <FaChevronLeft /> Volver a Morosos
                        </button>
                        <button
                            type="button"
                            className="rm-btn-excel"
                            onClick={handleGenerarExcel}
                            title="Descargar libro Excel .xlsx"
                        >
                            <FaFileExcel /> Descargar Excel
                        </button>
                        <button
                            type="button"
                            className="rm-btn-primary"
                            onClick={handleGenerarPDF}
                            title="Generar y descargar informe ejecutivo en PDF"
                        >
                            <FaFilePdf /> Descargar PDF
                        </button>
                    </div>
                </header>

                {/* TARJETAS KPI SUPERIORES */}
                <section className="rm-kpis-grid">
                    <div className="rm-kpi-card">
                        <div className="rm-kpi-top">
                            <span className="rm-kpi-label">Total Viviendas</span>
                            <div className="rm-kpi-icon blue"><FaHome /></div>
                        </div>
                        <div className="rm-kpi-val">{loading ? '...' : metricasGlobales.total}</div>
                        <div className="rm-kpi-sub">Padrón total activo</div>
                    </div>

                    <div className="rm-kpi-card">
                        <div className="rm-kpi-top">
                            <span className="rm-kpi-label">Al Corriente</span>
                            <div className="rm-kpi-icon green"><FaCheckCircle /></div>
                        </div>
                        <div className="rm-kpi-val" style={{ color: '#059669' }}>
                            {loading ? '...' : metricasGlobales.alCorriente}
                        </div>
                        <div className="rm-kpi-sub">
                            {metricasGlobales.total > 0
                                ? ((metricasGlobales.alCorriente / metricasGlobales.total) * 100).toFixed(1) + '% del total'
                                : '0%'}
                        </div>
                    </div>

                    <div className="rm-kpi-card">
                        <div className="rm-kpi-top">
                            <span className="rm-kpi-label">Viviendas en Mora</span>
                            <div className="rm-kpi-icon red"><FaExclamationTriangle /></div>
                        </div>
                        <div className="rm-kpi-val" style={{ color: '#dc2626' }}>
                            {loading ? '...' : metricasGlobales.morosos}
                        </div>
                        <div className="rm-kpi-sub">
                            Tasa de morosidad: <strong>{metricasGlobales.tasaMorosidad.toFixed(1)}%</strong>
                        </div>
                    </div>

                    <div className="rm-kpi-card">
                        <div className="rm-kpi-top">
                            <span className="rm-kpi-label">Cartera Vencida</span>
                            <div className="rm-kpi-icon amber"><FaCoins /></div>
                        </div>
                        <div className="rm-kpi-val" style={{ fontSize: '1.45rem' }}>
                            {loading ? '...' : formatCurrency(metricasGlobales.totalAdeudo)}
                        </div>
                        <div className="rm-kpi-sub">Adeudo total acumulado</div>
                    </div>
                </section>

                {/* PANEL DE CONFIGURACIÓN DE RANGOS (1 A 4 VISTAS) */}
                <section className="rm-config-section">
                    <div className="rm-section-header">
                        <div className="rm-section-title">
                            <FaSlidersH style={{ color: '#4f46e5', fontSize: '20px' }} />
                            <div>
                                <h2>Configurador de Rangos de Morosidad</h2>
                                <p>Personaliza los umbrales para agrupar y visualizar los adeudos en tiempo real</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <div className="rm-rangos-selector">
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', padding: '0 6px' }}>Vistas:</span>
                                {[1, 2, 3, 4].map((num) => (
                                    <button
                                        key={num}
                                        type="button"
                                        className={`rm-rango-pill ${numRangos === num ? 'active' : ''}`}
                                        onClick={() => handleCambiarNumRangos(num)}
                                    >
                                        {num} {num === 1 ? 'Rango' : 'Rangos'}
                                    </button>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="rm-btn-back"
                                onClick={handleRestablecerRangos}
                                title="Volver a los valores de la plantilla inicial"
                            >
                                <FaSyncAlt /> Restablecer
                            </button>
                        </div>
                    </div>

                    {/* TARJETAS INTERACTIVAS DE RANGOS */}
                    <div className="rm-rangos-cards-grid">
                        {analisisRangos.rangosCalculados.map((rango, idx) => (
                            <div key={rango.id} className="rm-rango-item-card" style={{ borderTop: `4px solid ${rango.color}` }}>
                                <div className="rm-rango-badge-header">
                                    <span className="rm-rango-badge" style={{ background: rango.color }}>
                                        Rango {idx + 1}
                                    </span>
                                    <div className="rm-rango-stats-quick">
                                        <span className="rm-rango-count">{rango.conteo}</span>
                                        <span className="rm-rango-pct">({rango.porcentajeCasas.toFixed(1)}%)</span>
                                    </div>
                                </div>

                                <div className="rm-input-group">
                                    <label className="rm-input-label">Nombre del Rango / Segmento:</label>
                                    <input
                                        type="text"
                                        className="rm-input-text"
                                        value={rango.etiqueta}
                                        onChange={(e) => handleActualizarRango(idx, 'etiqueta', e.target.value)}
                                        placeholder="Ej. Mora Aceptable por ley"
                                    />
                                </div>

                                {rango.esCeroOSaldoFavor ? (
                                    <div className="rm-rango-info-box">
                                        <span className="rm-rango-info-tag">Condición especial</span>
                                        <p>Aplica a viviendas al corriente (adeudo $0.00) o con saldo a favor registrado.</p>
                                    </div>
                                ) : (
                                    <div className="rm-range-inputs-vertical">
                                        <div className="rm-input-group">
                                            <label className="rm-input-label">Monto Mínimo ($):</label>
                                            <input
                                                type="number"
                                                className="rm-input-text"
                                                value={rango.min}
                                                onChange={(e) => handleActualizarRango(idx, 'min', Number(e.target.value))}
                                            />
                                        </div>

                                        <div className="rm-input-group">
                                            <label className="rm-input-label">Monto Máximo ($):</label>
                                            <input
                                                type="number"
                                                className="rm-input-text"
                                                value={rango.sinLimite ? '' : rango.max}
                                                disabled={rango.sinLimite}
                                                placeholder={rango.sinLimite ? 'En adelante' : '0.00'}
                                                onChange={(e) => handleActualizarRango(idx, 'max', Number(e.target.value))}
                                            />
                                            <label className="rm-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={rango.sinLimite}
                                                    onChange={(e) => handleActualizarRango(idx, 'sinLimite', e.target.checked)}
                                                />
                                                <span>Sin límite superior</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="rm-rango-money-total">
                                    <span style={{ color: '#64748b' }}>Cartera en rango:</span>
                                    <span className="rm-rango-money-val">{formatCurrency(rango.montoTotal)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SECCIÓN DE GRÁFICAS INTERACTIVAS */}
                <section className="rm-charts-container">
                    {/* GRÁFICA 1: RANGOS DE MOROSIDAD (BARRAS / PASTEL) */}
                    <div className="rm-chart-card">
                        <div className="rm-chart-header">
                            <div className="rm-chart-title">
                                <h3>Distribución de Viviendas por Rango</h3>
                                <p>Visualización del volumen y participación de cada segmento</p>
                            </div>
                            <div className="rm-chart-actions">
                                <div className="rm-chart-type-toggle">
                                    <button
                                        type="button"
                                        className={`rm-chart-toggle-btn ${tipoGrafica === 'barras' ? 'active' : ''}`}
                                        onClick={() => setTipoGrafica('barras')}
                                    >
                                        <FaChartBar /> Barras
                                    </button>
                                    <button
                                        type="button"
                                        className={`rm-chart-toggle-btn ${tipoGrafica === 'pastel' ? 'active' : ''}`}
                                        onClick={() => setTipoGrafica('pastel')}
                                    >
                                        <FaChartPie /> Pastel
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                {tipoGrafica === 'barras' ? (
                                    <BarChart data={chartRangosBarData} margin={{ top: 20, right: 20, left: 0, bottom: 25 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="name"
                                            stroke="#64748b"
                                            fontSize={11}
                                            tickFormatter={(val) => val.length > 16 ? val.substring(0, 14) + '...' : val}
                                        />
                                        <YAxis stroke="#64748b" fontSize={11} />
                                        <Tooltip
                                            formatter={(value, name, item) => [
                                                `${value} casas (${item.payload.porcentaje}) - ${formatCurrency(item.payload.Monto)}`,
                                                'Casas'
                                            ]}
                                        />
                                        <Bar dataKey="Casas" radius={[8, 8, 0, 0]}>
                                            <LabelList dataKey="Casas" position="top" fill="#0f172a" fontSize={12} fontWeight={700} />
                                            {chartRangosBarData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                ) : (
                                    <PieChart>
                                        <Pie
                                            data={chartRangosPieData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={95}
                                            paddingAngle={4}
                                            label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                        >
                                            {chartRangosPieData.map((entry, index) => (
                                                <Cell key={`cell-pie-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name, item) => [
                                                `${value} viviendas (${formatCurrency(item.payload.monto)})`,
                                                name
                                            ]}
                                        />
                                        <Legend verticalAlign="bottom" height={36} iconType="circle" />
                                    </PieChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* GRÁFICA 2: RECUPERACIÓN DE CARTERA HISTÓRICA */}
                    <div className="rm-chart-card">
                        <div className="rm-chart-header">
                            <div className="rm-chart-title">
                                <h3>Recuperación Histórica de Cartera</h3>
                                <p>Tendencia de cobranza y abonos regularizados a lo largo del tiempo</p>
                            </div>
                            <div className="rm-chart-actions">
                                <div className="rm-chart-type-toggle">
                                    <button
                                        type="button"
                                        className={`rm-chart-toggle-btn ${tipoRecuperacion === 'mes' ? 'active' : ''}`}
                                        onClick={() => setTipoRecuperacion('mes')}
                                    >
                                        <FaCalendarAlt /> Mes
                                    </button>
                                    <button
                                        type="button"
                                        className={`rm-chart-toggle-btn ${tipoRecuperacion === 'anio' ? 'active' : ''}`}
                                        onClick={() => setTipoRecuperacion('anio')}
                                    >
                                        Año
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={recuperacionCarteraData} margin={{ top: 15, right: 20, left: 0, bottom: 15 }}>
                                    <defs>
                                        <linearGradient id="colorRecup" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="periodo" stroke="#64748b" fontSize={11} />
                                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(val) => `$${val / 1000}k`} />
                                    <Tooltip formatter={(value) => [formatCurrency(value), 'Monto Recuperado']} />
                                    <Area type="monotone" dataKey="recuperado" stroke="#059669" strokeWidth={3} fillOpacity={1} fill="url(#colorRecup)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* TOGGLE PARA INCLUIR EN DESCARGA PDF */}
                        <label className="rm-chart-switch-include">
                            <input
                                type="checkbox"
                                checked={incluirRecuperacionEnPDF}
                                onChange={(e) => setIncluirRecuperacionEnPDF(e.target.checked)}
                            />
                            Incluir esta gráfica de Recuperación en el Reporte PDF descargable
                        </label>
                    </div>
                </section>

                {/* ANÁLISIS ESTRATÉGICO (ACUERDOS, AGING, PARETO) */}
                <section className="rm-strategic-grid">
                    {/* CONVENIOS / ACUERDOS (COLUMN T & U) */}
                    <div className="rm-strategic-card">
                        <div className="rm-strategic-header">
                            <FaHandshake style={{ color: '#059669', fontSize: '18px' }} />
                            <h4>Convenios y Acuerdos de Pago</h4>
                        </div>
                        <div className="rm-strategic-content">
                            <div className="rm-aging-row" style={{ background: '#ecfdf5' }}>
                                <span className="rm-aging-tag" style={{ color: '#047857' }}>Con Acuerdo Vigente:</span>
                                <span className="rm-aging-val" style={{ color: '#047857' }}>
                                    {metricasGlobales.conAcuerdo} casas ({metricasGlobales.morosos > 0 ? ((metricasGlobales.conAcuerdo / metricasGlobales.morosos) * 100).toFixed(1) : 0}%)
                                </span>
                            </div>
                            <div className="rm-aging-row" style={{ background: '#fef2f2' }}>
                                <span className="rm-aging-tag" style={{ color: '#b91c1c' }}>Sin Acuerdo Formal:</span>
                                <span className="rm-aging-val" style={{ color: '#b91c1c' }}>
                                    {metricasGlobales.sinAcuerdo} casas ({metricasGlobales.morosos > 0 ? ((metricasGlobales.sinAcuerdo / metricasGlobales.morosos) * 100).toFixed(1) : 0}%)
                                </span>
                            </div>
                            <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                                Permite distinguir qué porcentaje de la cartera en mora ya se encuentra bajo compromiso formal de amortización.
                            </p>
                        </div>
                    </div>

                    {/* AGING DE CARTERA */}
                    <div className="rm-strategic-card">
                        <div className="rm-strategic-header">
                            <FaCalendarAlt style={{ color: '#0284c7', fontSize: '18px' }} />
                            <h4>Antigüedad de Deuda (Aging)</h4>
                        </div>
                        <div className="rm-strategic-content">
                            {agingData.map((item, idx) => (
                                <div key={idx} className="rm-aging-row">
                                    <span className="rm-aging-tag">{item.label}</span>
                                    <span className="rm-aging-val">{item.count} casas &bull; {formatCurrency(item.monto)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* TOP DEUDORES / PARETO */}
                    <div className="rm-strategic-card">
                        <div className="rm-strategic-header">
                            <FaShieldAlt style={{ color: '#dc2626', fontSize: '18px' }} />
                            <h4>Concentración de Cartera (Top 5)</h4>
                        </div>
                        <div className="rm-strategic-content">
                            {topDeudores.list.map((c, idx) => (
                                <div key={idx} className="rm-aging-row">
                                    <span className="rm-aging-tag">Casa {c.numero} - {c.propietario.length > 18 ? c.propietario.substring(0, 16) + '...' : c.propietario}</span>
                                    <span className="rm-aging-val" style={{ color: '#dc2626' }}>{formatCurrency(c.adeudo)}</span>
                                </div>
                            ))}
                            <div style={{ marginTop: 'auto', paddingTop: '6px', borderTop: '1px solid #f1f5f9', fontSize: '0.82rem', color: '#475569' }}>
                                El Top 5 concentra el <strong>{topDeudores.pctTop5.toFixed(1)}%</strong> ({formatCurrency(topDeudores.sumaTop5)}) del adeudo total.
                            </div>
                        </div>
                    </div>
                </section>

                {/* TABLA DETALLADA DE VIVIENDAS */}
                <section className="rm-table-section">
                    <div className="rm-table-toolbar">
                        <div className="rm-search-box">
                            <FaSearch className="rm-search-icon" />
                            <input
                                type="text"
                                className="rm-search-input"
                                placeholder="Buscar por casa o nombre..."
                                value={filtroTabla}
                                onChange={(e) => setFiltroTabla(e.target.value)}
                            />
                        </div>

                        <div className="rm-table-filter-scope">
                            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>Alcance de Descarga:</label>
                            <select
                                className="rm-scope-select"
                                value={alcanceDescarga}
                                onChange={(e) => setAlcanceDescarga(e.target.value)}
                            >
                                <option value="morosos">Solo Viviendas con Adeudo</option>
                                <option value="todos">Padrón General Completo ({metricasGlobales.total} casas)</option>
                            </select>

                            <select
                                className="rm-scope-select"
                                value={rangoSeleccionadoFiltro}
                                onChange={(e) => setRangoSeleccionadoFiltro(e.target.value)}
                            >
                                <option value="todos">Todos los Rangos</option>
                                {analisisRangos.rangosCalculados.map((r) => (
                                    <option key={r.id} value={r.id}>{r.etiqueta} ({r.conteo})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="rm-table-wrapper">
                        <table className="rm-table">
                            <thead>
                                <tr>
                                    <th>Casa</th>
                                    <th>Residente / Propietario</th>
                                    <th style={{ textAlign: 'center' }}>Meses</th>
                                    <th style={{ textAlign: 'center' }}>Condición</th>
                                    <th style={{ textAlign: 'right' }}>Capital</th>
                                    <th style={{ textAlign: 'right' }}>Intereses</th>
                                    <th style={{ textAlign: 'right' }}>Cuotas Ext.</th>
                                    <th style={{ textAlign: 'right' }}>Adeudo Total</th>
                                    <th style={{ textAlign: 'center' }}>Convenio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {casasFiltradasTabla.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                                            No se encontraron viviendas con los criterios seleccionados.
                                        </td>
                                    </tr>
                                ) : (
                                    casasFiltradasTabla.map((c) => (
                                        <tr key={c.id}>
                                            <td style={{ fontWeight: 700, color: '#0f172a' }}>{c.numero}</td>
                                            <td>{c.propietario}</td>
                                            <td style={{ textAlign: 'center' }}>{c.mesesAdeudoEstimados} mes(es)</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={`rm-badge-pill ${c.esAbonoParcial ? 'success' : 'neutral'}`}>
                                                    {c.esAbonoParcial ? 'Abono Parcial' : 'Total Pendiente'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(c.adeudoCapital)}</td>
                                            <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatCurrency(c.adeudoIntereses)}</td>
                                            <td style={{ textAlign: 'right', color: '#d97706' }}>{formatCurrency(c.cuotasExtras)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                                                {formatCurrency(c.adeudo)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={`rm-badge-pill ${c.tieneConvenio ? 'info' : 'warning'}`}>
                                                    {c.tieneConvenio ? 'Con Acuerdo' : 'Sin Acuerdo'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* PIE DE PÁGINA INSTITUCIONAL DE PLATAFORMA DOMUS HABITA */}
                <footer className="rm-footer-platform">
                    <div className="rm-platform-brand">
                        <img
                            src="/Domus Icono1.png"
                            onError={(e) => { e.target.src = '/logo.png'; }}
                            alt="Logo Domus Habita"
                            className="rm-platform-logo-img"
                        />
                        <div className="rm-platform-brand-text">
                            <strong>Domus Habita</strong>
                            <span>Plataforma Integral de Administración de Condominios y Fraccionamientos</span>
                        </div>
                    </div>
                    <div>
                        Módulo de Auditoría y Morosidad &bull; Versión 1.0.3 &bull; México
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default ReporteMorosidadConfigurable;
